import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export enum PreloadStrategy {
    PRIORITY = 'priority'
}
@Injectable({
    providedIn: 'root'
})
export class ShPreloadingStrategyService implements PreloadingStrategy {
    private readonly IS_FAST_CONNECTION: boolean = this.isFastConnection();
    private readonly DELAY: number = 5000;

    preload(route: Route, load: () => Observable<any>): Observable<any> {
        if (!this.IS_FAST_CONNECTION) {
            return of(null);
        }

        if (route.path === null) {
            return of(null);
        }

        const isAccessible$: Observable<boolean> = route.canActivate ? this.isAccessible(route.canActivate) : of(true);

        return isAccessible$.pipe(
            switchMap(isAccessible => {
                if (!isAccessible) {
                    return of(null);
                }

                const preloadStrategy: PreloadStrategy | null = route.data?.['preload'];

                if (!preloadStrategy) {
                    // Default load lazy modules after seconds idle
                    return timer(this.DELAY).pipe(switchMap(() => load()));
                }

                if (preloadStrategy === PreloadStrategy.PRIORITY) {
                    return load();
                }

                console.error(
                    `ShPreloadingStrategyService:: No preloading strategy configured for '${preloadStrategy}' on route ${route.path}`
                );
                return of(null);
            })
        );
    }

    private isFastConnection(): boolean {
        const connection = (navigator as any).connection;
        return connection?.effectiveType === '4g' || !connection?.saveData;
    }

    private isAccessible(conditions: any[]): Observable<boolean> {
        return combineLatest(
            conditions.map(condition =>
                'isAccessible' in condition ? (condition?.isAccessible() as Observable<boolean>) : of(true)
            )
        ).pipe(map((accessible: boolean[]) => accessible.every(accessible => accessible)));
    }
}
